import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import SEOHead from '@/components/SEOHead';
import { validateSignIn, validateSignUp, getFirstError } from '@/utils/authValidation';
import { useAnalytics } from '@/hooks/useAnalytics';

const Auth = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signUpSuccessEmail, setSignUpSuccessEmail] = useState<string | null>(null);
  
  const { signUp, signIn } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSignUp) {
        // Validate sign-up data with zod schema
        const validationResult = validateSignUp({ email, password, fullName });
        if (!validationResult.success) {
          const errorMsg = getFirstError(validationResult);
          toast({
            title: "Error de validación",
            description: errorMsg || "Por favor verifica los datos ingresados",
            variant: "destructive"
          });
          return;
        }

        const { error } = await signUp(email, password, fullName.trim());
        if (error) {
          if (error.message.includes('already registered')) {
            toast({
              title: "Usuario ya registrado",
              description: "Este email ya está registrado. Intenta iniciar sesión.",
              variant: "destructive"
            });
          } else {
            toast({
              title: "Error en el registro",
              description: error.message,
              variant: "destructive"
            });
          }
        } else {
          setSignUpSuccessEmail(email);
          trackEvent('register');
          toast({
            title: "Revisa tu correo",
            description: `Te enviamos un enlace de confirmación a ${email}. Revisa tu bandeja de entrada y spam.`,
          });
        }
      } else {
        // Validate sign-in data with zod schema
        const validationResult = validateSignIn({ email, password });
        if (!validationResult.success) {
          const errorMsg = getFirstError(validationResult);
          toast({
            title: "Error de validación",
            description: errorMsg || "Por favor verifica los datos ingresados",
            variant: "destructive"
          });
          return;
        }

        const { error } = await signIn(email, password);
        if (error) {
          if (error.message.includes('Invalid login credentials')) {
            toast({
              title: "Credenciales incorrectas",
              description: "Email o contraseña incorrectos",
              variant: "destructive"
            });
          } else {
            toast({
              title: "Error al iniciar sesión",
              description: error.message,
              variant: "destructive"
            });
          }
        } else {
          toast({
            title: "¡Bienvenido!",
            description: "Has iniciado sesión correctamente",
          });
          navigate('/');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <SEOHead 
        title="Iniciar Sesión - Compresor de Imágenes | CompresorJPEG"
        description="Inicia sesión en CompresorJPEG para acceder a funciones premium, historial de compresión y más herramientas de optimización de imágenes."
        keywords="login compresor imagenes, iniciar sesion jpg, cuenta premium comprimir, acceso historial fotos"
        canonical="https://compresorjpeg.com/auth"
      />
      <div className="w-full max-w-md">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al inicio
        </Button>

        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-gray-900">
              {isSignUp ? 'Crear cuenta' : 'Iniciar sesión'}
            </CardTitle>
            <CardDescription>
              {isSignUp 
                ? 'Regístrate para comenzar a comprimir tus imágenes'
                : 'Accede a tu cuenta para continuar'
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <Tabs value={isSignUp ? 'signup' : 'signin'} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger 
                  value="signin" 
                  onClick={() => setIsSignUp(false)}
                >
                  Iniciar sesión
                </TabsTrigger>
                <TabsTrigger 
                  value="signup" 
                  onClick={() => setIsSignUp(true)}
                >
                  Registrarse
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="signin" className="space-y-4 mt-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">Contraseña</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Tu contraseña"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={loading}
                  >
                    {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="signup" className="space-y-4 mt-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Nombre completo</Label>
                    <Input
                      id="fullName"
                      type="text"
                      placeholder="Tu nombre completo"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">Contraseña</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Mínimo 6 caracteres"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={loading}
                  >
                    {loading ? 'Creando cuenta...' : 'Crear cuenta'}
                  </Button>

                  {signUpSuccessEmail && (
                    <div className="mt-4 text-sm text-gray-700">
                      <p className="mb-2">
                        Hemos enviado un correo de confirmación a <strong>{signUpSuccessEmail}</strong>.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={async () => {
                          const { error } = await supabase.auth.resend({ type: 'signup', email: signUpSuccessEmail });
                          if (error) {
                            toast({ title: 'No se pudo reenviar', description: error.message, variant: 'destructive' });
                          } else {
                            toast({ title: 'Correo reenviado', description: 'Revisa tu bandeja de entrada y spam.' });
                          }
                        }}
                      >
                        Reenviar correo de confirmación
                      </Button>
                    </div>
                  )}
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
